import { useEffect } from 'react';
import type { StyleSample } from '../types';
import { shopifyImg } from '../utils/img';

interface Props {
  samples: StyleSample[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const TEAL = 'var(--teal, #a8763e)';

/**
 * StyleSelector — 2-column grid of 3:4 portrait style cards (matches WooCommerce).
 *
 * The card height is driven by the <img> itself (width:100% + aspect-ratio:3/4),
 * which renders reliably across browsers — unlike an aspect-ratio'd <button>
 * whose children are all absolutely positioned (that collapsed to a thin line).
 * Style name overlays the bottom of the image; selected cards get a teal
 * border + a checkmark in the top-right corner.
 *
 * Reads `?style=<id>` on first render and auto-selects a matching sample.
 */
export function StyleSelector({ samples, selectedId, onSelect }: Props) {
  useEffect(() => {
    if (selectedId || samples.length === 0) return;
    const fromUrl = new URLSearchParams(window.location.search).get('style');
    if (!fromUrl) return;
    const target = samples.find((s) => s.id === fromUrl);
    if (target) onSelect(target.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (samples.length === 0) {
    return <p className="text-warm-brown/70 text-sm">No styles configured for this product.</p>;
  }

  return (
    <div
      role="radiogroup"
      aria-label="Portrait style"
      style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}
    >
      {samples.map((sample, index) => {
        const isSelected = sample.id === selectedId;
        const label = sample.badge ?? sample.name ?? sample.id;
        // gallery convention: "img1,img2,img3,img4" — [0] catalog/home,
        // [1] product-page style card (this one), [2]+[3] shown while generating.
        const galParts = sample.gallery?.[0]?.split(',').map((s) => s.trim()).filter(Boolean) ?? [];
        const cardGallery = galParts[1] || galParts[0]; // img2, fallback img1
        const rawImage = cardGallery || sample.image || sample.icon_url;
        // Unmigrated metafields still carry "<<CDN:…>>" tokens — treat as no image.
        // Serve a 500px-wide CDN variant (cards are ≤380px @1×) instead of the
        // full-res original — the biggest single mobile-LCP win.
        const imageUrl = rawImage && !rawImage.startsWith('<<') ? shopifyImg(rawImage, 500) : null;
        // The first row is above the fold and holds the LCP element — load it
        // eagerly with high priority; lazy-load the rest.
        const aboveFold = index < 2;
        return (
          <button
            key={sample.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(sample.id)}
            className={[
              'group transition-transform hover:-translate-y-0.5',
              isSelected ? 'bf-glow' : '',
            ].join(' ')}
            style={{
              position: 'relative',
              display: 'block',
              padding: 0,
              cursor: 'pointer',
              borderRadius: 12,
              overflow: 'hidden',
              textAlign: 'left',
              border: isSelected ? `3px solid ${TEAL}` : '3px solid transparent',
              // Selected cards get the pulsing gold glow (bf-glow keyframes);
              // its animated box-shadow overrides this static one.
              boxShadow: isSelected ? undefined : '0 2px 8px rgba(100,70,30,0.10)',
              background: '#ede5d5',
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                loading={aboveFold ? 'eager' : 'lazy'}
                fetchPriority={aboveFold ? 'high' : 'auto'}
                decoding="async"
                style={{ display: 'block', width: '100%', aspectRatio: '3 / 4', objectFit: 'cover' }}
              />
            ) : (
              <div
                style={{
                  display: 'grid',
                  placeItems: 'center',
                  width: '100%',
                  aspectRatio: '3 / 4',
                  padding: 16,
                  textAlign: 'center',
                  color: '#fff',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #a8763e 0%, #8f6133 100%)',
                }}
              >
                {label}
              </div>
            )}

            {/* Style name — overlaid at the bottom of the image */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                padding: '28px 12px 10px',
                background: 'linear-gradient(transparent, rgba(0,0,0,0.62))',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1.2,
                pointerEvents: 'none',
              }}
            >
              {label}
            </div>

            {/* Checkmark — top-right when selected */}
            {isSelected && (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: TEAL,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
